import { modelOptions, post, prop } from "@typegoose/typegoose";
import { z } from "zod";
import { getZodSchemaFromClass, validate } from "../../../zod-decorator/index.js";
import { addSchemaKeyHook, getDefaultSchemaOptions, SchemaBase, SchemaConstructorArgs } from "../../schemaBase.js";
import {
    CurrentIntendedStateFieldsWithDefaults,
    CurrentIntendedStateSchemaTypes,
} from "../../types/currentIntendedState.d.js";
import { DtoValidatorTypes } from "../../validatableClass.js";

const SCHEMA_KEY = "CurrentIntendedState" as const;

@modelOptions({ schemaOptions: { ...getDefaultSchemaOptions(SCHEMA_KEY) } })
@post<SchemaBase<CurrentIntendedState>>(...addSchemaKeyHook(SCHEMA_KEY))
export class CurrentIntendedState extends SchemaBase<CurrentIntendedState, CurrentIntendedStateFieldsWithDefaults> {
    static readonly schemaKey = SCHEMA_KEY;

    @validate({ schemaKey: SCHEMA_KEY })
    readonly schemaKey = SCHEMA_KEY;

    /**
     * @deprecated
     * @type {boolean}
     */
    @validate({ zodValidator: z.boolean().optional() })
    @prop({ type: () => Boolean, required: true, default: false })
    irrigating?: boolean = false;

    // the last time that the state of the zone was certain and was closed
    @validate({ zodValidator: z.coerce.date().optional() })
    @prop({ type: () => Date, required: true, default: new Date(0) })
    lastClosed: Date = new Date(0);

    // the last time that the state of the zone was certain and was open
    @validate({ zodValidator: z.coerce.date().optional() })
    @prop({ type: () => Date, required: true, default: new Date(0) })
    lastOpen: Date = new Date(0);

    // the last time that the state of the zone was uncertain due to a schedule change.
    @validate({ zodValidator: z.coerce.date().optional() })
    @prop({ type: () => Date, required: true, default: new Date(0) })
    lastUncertain: Date = new Date(0);

    @validate({ zodValidator: z.coerce.date().optional() })
    @prop({ type: () => Date, required: true, default: new Date(0) })
    lastChange: Date = new Date(0);

    @validate({ zodValidator: z.coerce.date().optional() })
    @prop({ type: () => Date, required: true, default: new Date(0) })
    lastUpdated: Date = new Date(0);

    @validate({ zodValidator: z.boolean().optional() })
    @prop({ type: () => Boolean, required: true, default: false })
    ideallyCurrentlyOpen: boolean = false;

    @validate({ zodValidator: z.boolean().optional() })
    @prop({ type: () => Boolean, required: true, default: false })
    currentlyUncertain: boolean = false;

    @validate({ zodValidator: z.coerce.date().optional() })
    @prop({ type: () => Date, required: true, default: new Date(0) })
    timeOfScheduleModification: Date = new Date(0);

    /**
     * next time this zone is planned to close. Will be undefined if currently uncertain or if too far in future
     * @type {Date}
     */
    @validate({ zodValidator: z.coerce.date().optional() })
    @prop({ type: () => Date, required: false })
    nextClosed?: Date;

    /**
     * next time this zone is planned open Will be undefined if currently uncertain or if too far in future
     * @type {Date}
     */
    @validate({ zodValidator: z.coerce.date().optional() })
    @prop({ type: () => Date, required: false })
    nextOpen?: Date;

    /**
     * next known uncertain state. Will be undefined if currently uncertain or if too far in future
     * @type {Date}
     */
    @validate({ zodValidator: z.coerce.date().optional() })
    @prop({ type: () => Date, required: false })
    nextUncertain?: Date;

    /**
     * next time this zone will change between being open, closed, or uncertain.
     * Will be undefined if it is too far in the future.
     * @type {Date}
     */
    @validate({ zodValidator: z.coerce.date().optional() })
    @prop({ type: () => Date, required: false })
    nextChange?: Date;

    /**
     * Denotes if a zone has no impending changes, hasn't changed
     * too recently, and is not in an uncertain state. Means it is
     * an ideal time to verify devices in the zone.
     * @type {boolean}
     */
    @validate({ zodValidator: z.boolean().optional() })
    @prop({ type: () => Boolean, required: true, default: false })
    isStable: boolean = false;

    /**
     * Records the time that this zone last transitioned from unstable
     * to stable
     * @type {boolean}
     */
    @validate({ zodValidator: z.coerce.date().optional() })
    @prop({ type: () => Date, required: true, default: new Date(0) })
    becameStable: Date = new Date(0);

    @validate({ zodValidator: z.coerce.date().optional() })
    @prop({ type: () => Date, required: true, default: new Date(0) })
    changingAt?: Date = new Date(0);

    @validate({ zodValidator: z.boolean().optional() })
    @prop({ type: () => Boolean, required: true, default: false })
    nextStateUncertain?: boolean = false;

    @validate({ zodValidator: z.coerce.date().optional() })
    @prop({ type: () => Date, required: true, default: new Date(0) })
    updatedAt?: Date = new Date(0);

    static dtoValidators: DtoValidatorTypes<CurrentIntendedStateSchemaTypes>;

    constructor(properties?: SchemaConstructorArgs<CurrentIntendedState, CurrentIntendedStateFieldsWithDefaults>) {
        super(properties);
        Object.assign(this, properties);
    }
}

CurrentIntendedState.dtoValidators = getZodSchemaFromClass(
    CurrentIntendedState,
) as unknown as DtoValidatorTypes<CurrentIntendedStateSchemaTypes>;
